import React, { useMemo } from 'react';

import { useAppSelector } from '@fd/customHooks/useAppSelector';
import useQueryRMSCommonDataHook from '@fd/customHooks/useQueryRMSCommonDataHook';
import { SalesChannelType } from '@fd/customHooks/useQueryRMSSalesChannels';
import { ReportVisibilityResult, useReportVisibility } from '@fd/customHooks/useReportVisibility';
import StoreIcon from '@mui/icons-material/StoreOutlined';
import { getTranslate } from 'react-localize-redux';

import { useIsPhoneNumbersPageOn } from '../../../../components/PhoneAgent/phoneNumbersPage';
import { getPropertiesPermissions } from '../../../../components/Properties/propertiesPermissions';
import {
  getPropertyDevicesMenuItems,
  getPropertySettingsMenuItems,
} from '../../../../components/Properties/utils';
import useLookerDashboards from '../../../../components/Reports/customHooks/useLookerDashboards';
import {
  getReportsMenuItems,
  VisibilityContext,
} from '../../../../components/Reports/utils/getReportsMenuItems';
import { getOrganizationMenuItems } from '../../../../components/RMS/Organization/utils/getOrganizationMenuItems';
import {
  getIsFlipdishChannel,
  getSalesChannelSettingBaseUrl,
} from '../../../../components/RMS/SalesChannels/utils';
import { getSalesChannelMenuItems } from '../../../../components/RMS/utils/getSalesChannelMenuItems';
import { RMSSubscriptions } from '../../../../components/RMS/utils/rmsSubscriptions';
import { getFlipdishSalesChannelMenuItems } from '../../../../components/Stores/utils/getFlipdishSalesChannelMenuItems';
import { entitlementConstants } from '../../../../constants/entitlement.constants';
import useHasEntitlement from '../../../../custom-hooks/useHasEntitlement';
import { isProductBasedMenusEnabled } from '../../../../selectors/app.selector';
import { PortalFeatureFlags } from '../../../../services/featureFlags/portalFeatureFlags';
import { useBooleanFeatureFlagResolution } from '../../../../services/featureFlags/useBooleanFeatureFlagResolution';
import { flagService } from '../../../../services/flagService/flagService';
import { PortalViewId } from '../../../../viewConfiguration';
import { useMenuItemsConfig } from '../../sidebar/menuItemsConfig';
import { flattenMenuItems } from '../utils/searchItems.utils';

// TODO: Remove when reportCategoryVisibility flag is removed (keep only category mode)
function buildVisibilityContext(reportVisibility: ReportVisibilityResult): VisibilityContext {
  if (reportVisibility.mode === 'category') {
    const category =
      reportVisibility.categoryResult.status === 'categorized'
        ? reportVisibility.categoryResult.category
        : 'ONLINE_ONLY_NON_RMS';
    return { mode: 'category', category, subscriptions: reportVisibility.subscriptions };
  }
  return {
    mode: 'legacy',
    menuSource: reportVisibility.menuSource,
    subscriptions: reportVisibility.subscriptions,
  };
}

const useSearchMenuItems = () => {
  const translate = useAppSelector((state) => getTranslate(state.locale));
  const currentApp = useAppSelector((state) => state.currentApp);
  const permissions = useAppSelector((state) => state.permissions || []);
  const isShowCollectionSettingsAtPropertyLevelOn = useAppSelector((state) =>
    flagService.isFlagOn(state, 'showCollectionSettingsAtPropertyLevel')
  );
  const isProductBasedMenusOn = useAppSelector((state) => isProductBasedMenusEnabled(state));
  const isPhoneNumbersPageOn = useIsPhoneNumbersPageOn();
  const orgId = useAppSelector((state) => state.rms.currentOrg?.orgId);
  const { hasEntitlement: hasPhoneAgentEntitlement } = useHasEntitlement(
    orgId,
    entitlementConstants.PhoneAgent.AiAgent
  );
  const { data: rmsData } = useQueryRMSCommonDataHook(currentApp?.AppId || '');
  const subscriptions = rmsData?.subscriptions;
  const menuSource = rmsData?.associations?.menuSource;
  const reportVisibility = useReportVisibility(currentApp?.AppId || '');

  // Fetch available Looker dashboards to determine which dynamic reports are available
  const { data: availableDashboardsData = [] } = useLookerDashboards(currentApp?.AppId || '');

  const properties = rmsData?.associations?.orgHierarchy?.properties;
  const useAuthZForGetApps = useBooleanFeatureFlagResolution(
    PortalFeatureFlags.backend_auth_useAuthZForGetApps,
    false
  );
  const useAuthZPermissions = useAuthZForGetApps.status === 'resolved' && useAuthZForGetApps.value;
  const propertiesPermissions = getPropertiesPermissions(useAuthZPermissions);
  const enabledProperties = useMemo(
    () => properties?.filter((property) => property.isEnabled) ?? [],
    [properties]
  );

  const propertyMenuItems = useMemo(() => {
    if (!properties?.length) return { propertyItems: [], propertyDevices: [] };
    const appId = currentApp?.AppId || '';
    const hasBaseSubscription = (rmsData?.subscriptions || []).includes(RMSSubscriptions.BASE);

    const propertyItems = enabledProperties.map((property) => ({
      title: property.name,
      path: `/${appId}/properties/${property.code}`,
      icon: <StoreIcon />,
      propertyId: property.code,
      propertyName: property.name,
      searchAlias: [property.name, property.code, 'property', 'restaurant', 'store'],
      permissions: propertiesPermissions,
      viewId: PortalViewId.PropertiesDetails,
      noTranslate: true,
    }));

    const propertySettingsItems = enabledProperties.flatMap((property) =>
      getPropertySettingsMenuItems({
        appId,
        property: { propertyId: property.code, name: property.name },
        isSearchItems: true,
        isShowCollectionSettingsAtPropertyLevelOn,
        isPhoneNumbersPageOn,
      })
    );

    const propertyDevices = enabledProperties.flatMap((property) =>
      getPropertyDevicesMenuItems({
        appId,
        property: { propertyId: property.code, name: property.name },
        hasBaseSubscription,
        loadAllItemsForProperty: false,
      })
    );

    return { propertyItems, propertySettingsItems, propertyDevices };
  }, [
    enabledProperties,
    currentApp?.AppId,
    rmsData?.subscriptions,
    isShowCollectionSettingsAtPropertyLevelOn,
    isPhoneNumbersPageOn,
    propertiesPermissions,
  ]);

  const salesChannelItems = useMemo(
    () =>
      enabledProperties.flatMap((property) =>
        property.storefronts
          ?.filter((storefront) => {
            // Filter out archived storefronts
            if (storefront.isArchived) return false;

            if (
              storefront.type === SalesChannelType.FLIPDISH_PHONE_AGENT &&
              !hasPhoneAgentEntitlement
            ) {
              return false;
            }

            // For Flipdish channels, filter out linked ones (they don't have their own storeId)
            // For marketplace channels, keep them regardless (they naturally don't have storeId)
            if (getIsFlipdishChannel(storefront.type as SalesChannelType)) {
              // Flipdish channels without storeId are linked sales channels
              return !!storefront.fieldAssociations?.flipdish?.storeId;
            }

            // Keep all non-Flipdish channels (marketplaces, POS, etc.)
            return true;
          })
          ?.flatMap((storefront) => {
            const baseUrl = getSalesChannelSettingBaseUrl({
              appId: currentApp?.AppId || '',
              propertyId: property.code,
              channelId: storefront.code,
              channelType: storefront.type as SalesChannelType,
            });
            const storeId = storefront.fieldAssociations?.flipdish?.storeId
              ? String(storefront.fieldAssociations.flipdish.storeId)
              : undefined;

            return getIsFlipdishChannel(storefront.type as SalesChannelType)
              ? getFlipdishSalesChannelMenuItems({
                  // Staff-only items that link outside the channel settings (Fee config,
                  // kiosk card reader branding) need appId, propertyId and storeId
                  appId: currentApp?.AppId || '',
                  propertyId: property.code,
                  storeId,
                  baseUrl,
                  salesChannelType: storefront.type as SalesChannelType,
                  storefrontInformation: storefront,
                  linkedSalesChannelId: undefined,
                  isOldStorePage: false,
                  permissions,
                  isSearchItems: true,
                  isShowCollectionSettingsAtPropertyLevelOn,
                })
              : getSalesChannelMenuItems({
                  appId: currentApp?.AppId || '',
                  baseUrl,
                  salesChannelType: storefront.type as SalesChannelType,
                  storefrontInformation: storefront,
                  propertyId: property.code,
                  isSearchItems: true,
                  isShowCollectionSettingsAtPropertyLevelOn,
                  storeId,
                });
          })
      ),
    [enabledProperties, currentApp?.AppId, permissions, hasPhoneAgentEntitlement]
  );

  const organizationMenuItems = useMemo(() => {
    if (!currentApp?.AppId) return [];

    return getOrganizationMenuItems({
      baseUrl: `/${currentApp.AppId}/settings/organisation`,
      permissions,
    });
  }, [currentApp?.AppId, permissions]);

  const reportsMenuItems = useMemo(() => {
    if (!currentApp?.AppId || reportVisibility.isLoading) return [];

    if (
      reportVisibility.mode === 'category' &&
      reportVisibility.categoryResult.status === 'no_storefronts'
    ) {
      return [];
    }

    const visibilityContext = buildVisibilityContext(reportVisibility);
    return getReportsMenuItems(currentApp.AppId, availableDashboardsData, visibilityContext);
  }, [currentApp?.AppId, availableDashboardsData, reportVisibility]);

  const sideNavMenuItems = useMenuItemsConfig(
    currentApp,
    isProductBasedMenusOn,
    menuSource,
    subscriptions
  );

  const menuItems = flattenMenuItems(
    [
      ...sideNavMenuItems,
      ...propertyMenuItems.propertyItems,
      ...(propertyMenuItems.propertySettingsItems || []),
      ...propertyMenuItems.propertyDevices,
      ...salesChannelItems,
      ...organizationMenuItems,
      ...reportsMenuItems,
    ],
    translate
  );

  return { menuItems };
};

export default useSearchMenuItems;
